import React, { useState } from 'react'
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image'
import { PrismicRichText } from '@prismicio/react'
import CustomLink from 'src/utils/customLink'
import Eyebrow from 'src/images/svgs/Eyebrow.svg'
import { generateRandomId } from 'src/utils/domHelper'
import {
  PrismicLinkType,
  PrismicStructuredTextType,
} from 'src/typings/generated/graphql'
import OptionSelectModal, {
  OptionSelectModalContent,
} from './optionSelectModal'
import * as Styles from './collageStatsCallout.module.scss'

export enum CollageStatsCalloutPrimaryButtonVariant {
  link = 'link',
  modal = 'modal',
}

export type CollageImage = {
  image?: IGatsbyImageData
  alt?: string
}

export type CollageStat = {
  value?: string
  label?: string
}

export type CollageStatsCalloutContent = {
  anchor?: string
  image1?: CollageImage
  image2?: CollageImage
  image3?: CollageImage
  image4?: CollageImage
  headline?: string
  description?: PrismicStructuredTextType
  stats?: CollageStat[]
  primaryButtonText?: string
  primaryButtonVariant?: string
  primaryButtonLink?: PrismicLinkType
  secondaryButtonText?: string
  secondaryButtonLink?: PrismicLinkType
  modal?: OptionSelectModalContent
}

type Props = {
  content: CollageStatsCalloutContent
}

const renderImage = (image: CollageImage | undefined) => {
  if (!image?.image) return <div className={Styles.collageImage} />
  return (
    <div className={Styles.collageImage}>
      <GatsbyImage image={image.image} alt={image.alt ?? ''} />
    </div>
  )
}

const CollageStatsCallout = ({ content }: Props) => {
  const {
    anchor = generateRandomId(),
    image1,
    image2,
    image3,
    image4,
    headline = '',
    description,
    stats = [],
    primaryButtonText = '',
    primaryButtonVariant = CollageStatsCalloutPrimaryButtonVariant.link,
    primaryButtonLink,
    secondaryButtonText = '',
    secondaryButtonLink,
    modal,
  } = content

  const [isModalOpen, setIsModalOpen] = useState(false)

  const opensModal =
    primaryButtonVariant === CollageStatsCalloutPrimaryButtonVariant.modal

  const visibleStats = stats.filter(s => s?.value || s?.label)

  return (
    <section id={anchor} className={Styles.collageStatsCallout}>
      <div className={Styles.collage}>
        <div className={`${Styles.collageRow} ${Styles.topRow}`}>
          {renderImage(image1)}
          {renderImage(image2)}
        </div>
        <div className={`${Styles.collageRow} ${Styles.bottomRow}`}>
          {renderImage(image3)}
          {renderImage(image4)}
        </div>
      </div>
      <div className={Styles.content}>
        <div className={Styles.eyebrow}>
          <Eyebrow />
        </div>
        {headline && <h2 className={Styles.headline}>{headline}</h2>}
        {description?.richText && (
          <div className={Styles.description}>
            <PrismicRichText
              field={description.richText}
              components={{
                hyperlink: ({ node, children }: any) =>
                  CustomLink({ link: node.data, children }),
              }}
            />
          </div>
        )}
        {visibleStats.length > 0 && (
          <div className={Styles.stats}>
            {visibleStats.map((stat, index) => {
              const key = `stat-${index}-${stat.value ?? stat.label}`
              return (
                <div key={key} className={Styles.stat}>
                  {stat.value && (
                    <span className={Styles.statValue}>{stat.value}</span>
                  )}
                  {stat.label && (
                    <span className={Styles.statLabel}>{stat.label}</span>
                  )}
                </div>
              )
            })}
          </div>
        )}
        <div className={Styles.buttons}>
          {primaryButtonText &&
            (opensModal ? (
              <button
                type="button"
                className={Styles.primaryButton}
                onClick={() => setIsModalOpen(true)}
              >
                {primaryButtonText}
              </button>
            ) : (
              <CustomLink
                className={Styles.primaryButton}
                link={primaryButtonLink}
              >
                {primaryButtonText}
              </CustomLink>
            ))}
          {secondaryButtonText && (
            <CustomLink
              className={Styles.secondaryButton}
              link={secondaryButtonLink}
            >
              {secondaryButtonText}
            </CustomLink>
          )}
        </div>
      </div>
      {opensModal && modal && (
        <OptionSelectModal
          content={modal}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </section>
  )
}

export default CollageStatsCallout
